import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CookieConsentBanner from './CookieConsentBanner';

const STORAGE_KEY = 'mybricklog_cookie_consent_v1';

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [consent, setConsent] = useState('pending');
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setConsent('pending');
        setLoading(false);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.consent === 'essential' || parsed?.consent === 'all') {
        setConsent(parsed.consent);
      } else {
        setConsent('pending');
      }
    } catch {
      setConsent('pending');
    }
    setLoading(false);
  }, []);

  const persist = useCallback((value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: 1,
          consent: value,
          updatedAt: new Date().toISOString(),
        })
      );
    } catch {
      /* ignore quota / private mode */
    }
    setConsent(value);
    setSettingsOpen(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('mybricklog:cookieConsent', { detail: value })
      );
    }
  }, []);

  const acceptAll = useCallback(() => persist('all'), [persist]);
  const acceptEssentialOnly = useCallback(() => persist('essential'), [persist]);

  const openCookieSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  const closeBanner = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const bannerVisible =
    !loading && (consent === 'pending' || settingsOpen);

  const value = useMemo(
    () => ({
      consent,
      loading,
      acceptAll,
      acceptEssentialOnly,
      openCookieSettings,
      bannerVisible,
      closeBanner,
    }),
    [
      consent,
      loading,
      acceptAll,
      acceptEssentialOnly,
      openCookieSettings,
      bannerVisible,
      closeBanner,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      {!loading && (
        <CookieConsentBanner
          visible={bannerVisible}
          onAcceptAll={acceptAll}
          onAcceptEssentialOnly={acceptEssentialOnly}
          onClose={closeBanner}
          isFirstVisit={consent === 'pending'}
        />
      )}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}
